import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Logo } from "@/components/Logo";
import { Search, MapPin, Truck, PackageCheck, Clock, Loader2, CheckCircle2, Navigation } from "lucide-react";

export default function ConsignmentTrack() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get("q") || "");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const mapEl = useRef(null);
  const mapObj = useRef(null);
  const marker = useRef(null);

  const search = async (q) => {
    const term = (q ?? query).trim();
    if (!term) return;
    setBusy(true); setErr(""); setData(null);
    try {
      const { data } = await api.get(`/track-consignment`, { params: { q: term } });
      setData(data);
    } catch (e) { setErr(apiErr(e)); } finally { setBusy(false); }
  };

  useEffect(() => {
    const q = params.get("q");
    if (q) search(q);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const loc = data?.current_location;
    if (!loc || loc.lat == null || loc.lng == null) return;
    if (!mapEl.current) return;
    if (!mapObj.current) {
      mapObj.current = L.map(mapEl.current, { scrollWheelZoom: false }).setView([loc.lat, loc.lng], 12);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap contributors", maxZoom: 19,
      }).addTo(mapObj.current);
    }
    if (marker.current) mapObj.current.removeLayer(marker.current);
    marker.current = L.circleMarker([loc.lat, loc.lng], { radius: 10, color: "#10b981", fillColor: "#10b981", fillOpacity: 0.9, weight: 3 }).addTo(mapObj.current);
    marker.current.bindTooltip("Your shipment", { permanent: true, direction: "top", offset: [0, -10], className: "cons-label" });
    mapObj.current.setView([loc.lat, loc.lng], 12);
  }, [data]);

  const onSubmit = (e) => { e.preventDefault(); setParams(query.trim() ? { q: query.trim() } : {}); search(); };

  return (
    <div className="min-h-screen bg-background" data-testid="consignment-track-page">
      <style>{`.cons-label{background:#0f172a;color:#fff;border:none;box-shadow:0 1px 4px rgba(0,0,0,.4);font-weight:700;font-size:11px;padding:2px 8px;border-radius:2px}.cons-label::before{display:none}.leaflet-container{background:#1a1a1a;font-family:inherit}`}</style>
      <header className="h-16 border-b border-border bg-card sticky top-0 z-20 flex items-center justify-between px-6">
        <Link to="/"><Logo className="h-9" /></Link>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <Link to="/" className="text-sm font-bold uppercase tracking-wide border border-border px-4 py-2 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors" data-testid="get-quote-link">Get a quote</Link>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-10">
        <div className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Live Shipment Tracking</div>
        <h1 className="font-heading font-black text-4xl lg:text-5xl tracking-tight leading-[0.95]">Track your consignment.</h1>
        <p className="text-muted-foreground mt-3">Enter your <b>Order Number</b> or <b>LR Number</b> to see live status and location.</p>

        <form onSubmit={onSubmit} className="mt-6 flex gap-2" data-testid="consignment-search-form">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="rounded-none pl-9 h-12 font-mono-num" placeholder="e.g. LOG-2026-0001 or 16001" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="consignment-search-input" />
          </div>
          <Button type="submit" disabled={busy} className="rounded-none h-12 px-6 font-bold uppercase tracking-wide gap-2" data-testid="consignment-search-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Track
          </Button>
        </form>

        {err && <div className="mt-6 border border-destructive/40 bg-destructive/5 text-destructive p-4 text-sm" data-testid="consignment-error">{err}</div>}

        {data && (
          <div className="mt-8 space-y-6" data-testid="consignment-result">
            <div className="border border-border bg-card p-6">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Order No.</div>
                  <div className="font-mono-num font-bold text-primary text-lg" data-testid="cons-order-number">{data.order_number}</div>
                  {data.lr_number && <div className="text-xs text-muted-foreground mt-1">LR: <span className="font-mono-num">{data.lr_number}</span></div>}
                </div>
                <div className="text-right">
                  <div className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 text-sm font-bold uppercase tracking-wide" data-testid="cons-status">
                    <Truck className="h-4 w-4" /> {data.status_label}
                  </div>
                  {data.eta && <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1 justify-end"><Clock className="h-3 w-3" /> ETA: <span className="font-medium text-foreground">{data.eta}</span></div>}
                </div>
              </div>

              {data.current_location_text && (
                <div className="mt-4 border border-primary/30 bg-primary/5 px-4 py-3 flex items-start gap-2" data-testid="cons-current-location">
                  <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Current Location</div>
                    <div className="text-sm font-medium">{data.current_location_text}</div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm mt-6">
                <div className="border border-border p-4"><div className="text-xs uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1"><MapPin className="h-3 w-3" /> From</div>{data.pickup || "—"}</div>
                <div className="border border-border p-4"><div className="text-xs uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1"><MapPin className="h-3 w-3 text-primary" /> To</div>{data.delivery || "—"}</div>
              </div>
            </div>

            {/* Status timeline */}
            <div className="border border-border bg-card p-6" data-testid="cons-timeline">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-4">Progress</div>
              <div className="space-y-0">
                {data.statuses.map((s, i) => {
                  const curIdx = data.statuses.findIndex((x) => x.key === data.status);
                  const done = i <= curIdx;
                  const isCur = i === curIdx;
                  return (
                    <div key={s.key} className="flex items-start gap-3">
                      <div className="flex flex-col items-center">
                        <span className={`h-6 w-6 grid place-items-center rounded-full border-2 ${done ? "bg-emerald-500 border-emerald-500 text-white" : "border-border text-muted-foreground"}`}>
                          {done ? <CheckCircle2 className="h-4 w-4" /> : <span className="text-[10px]">{i + 1}</span>}
                        </span>
                        {i < data.statuses.length - 1 && <span className={`w-0.5 h-8 ${i < curIdx ? "bg-emerald-500" : "bg-border"}`} />}
                      </div>
                      <div className={`pt-0.5 text-sm ${isCur ? "font-bold text-foreground" : done ? "text-foreground" : "text-muted-foreground"}`}>{s.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Live location */}
            {data.current_location ? (
              <div className="border border-border bg-card" data-testid="cons-map-wrap">
                <div className="px-5 py-3 border-b border-border flex items-center gap-2 text-sm font-bold uppercase tracking-wide"><Navigation className="h-4 w-4 text-emerald-500" /> Live Location</div>
                <div ref={mapEl} style={{ height: 320, width: "100%" }} />
                <div className="px-5 py-2 text-[11px] text-muted-foreground">Updated {data.current_location.updated_at ? new Date(data.current_location.updated_at).toLocaleString() : "—"}</div>
              </div>
            ) : (
              <div className="border border-border bg-muted p-6 text-center text-sm text-muted-foreground flex items-center justify-center gap-2" data-testid="cons-no-location">
                <PackageCheck className="h-5 w-5 text-primary" /> Live GPS location will appear here once your driver starts the trip.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
